import i18n from '@/utils/i18n';
import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';
import { AppPageLayout } from '@experiences/templates/app-page-layout';
import { ConfidentialFieldToggle } from '@/experiences/sales-portal/components/confidential-field-toggle';
import { ContentWithPanelTemplate } from '@experiences/templates/content-with-panel-template';
import { createStyles } from '@/contexts/theme-context';
import { ErrorState } from '@/experiences/sales-portal/components/error-state';
import { ExperienceMode, useExperienceStore } from '@/stores/experience-store';
import { Experiments } from '@domain/configuration/experiments';
import {
  FulfillmentDropdown,
  FulfillmentDropdownTriggerProps,
} from '@/experiences/sales-portal/domain/ordering/components/fulfillment-dropdown';
import { FulfillmentDropdownSkeleton } from '@/experiences/sales-portal/domain/ordering/components/fulfillment-dropdown-skeleton';
import { FulfillmentTypeAndDateTrigger } from '@/experiences/sales-portal/domain/ordering/components/fulfillment-type-and-date-trigger';
import { isMinMaxQuantityEnabled } from '@/hooks/use-min-max-quantity-enabled';
import { ItemDetailContent } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/containers/item-detail-content';
import { ItemDetailPanelFooter } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/item-detail-content/item-detail-panel-footer';
import { ItemDetailTabKey } from '@/enums';
import { Keyboard, Platform, View } from 'react-native';
import { navigateToSalesPortalRoute } from '@/experiences/sales-portal/sales-portal-route-config';
import { orderEntryCollection } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/collections/order-entry-collection';
import { OrderEntryErrorBoundary } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-error-boundary';
import { OrderEntryFulfillmentAvailability } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-fulfillment-availability';
import { OrderEntryPriceEditDisplayHeader } from '@/types';
import { OrderEntryScreenContent } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/containers/order-entry-screen-content';
import { OrderEntryTableRowData } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/transformers/order-entry-table-transformers';
import { OrderEntryTableSkeleton } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-table/order-entry-table-skeleton';
import { partnerSupplier } from '@/utils/environment';
import { RestaurantListData } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/transformers/sales-portal-restaurant-transformer';
import { RestaurantNavigateButton } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/restaurant-navigate-button';
import { RestaurantSelector } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/restaurant-selector';
import { RestaurantSelectorSkeleton } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/restaurant-selector-skeleton';
import { SALES_PORTAL_ROUTES } from '@/navigators/routes';
import { SalesHubShellTemplate } from '@/experiences/sales-portal/templates/sales-hub-shell-template';
import { SalesPortalBanner } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/sales-portal-banner';
import {
  SalesPortalKeyboardScope,
  SalesPortalShortcuts,
} from '@/experiences/sales-portal/platform/shortcuts/sales-portal-shortcuts';
import { SalesPortalStackParams } from '@/navigators/types';
import { SearchSkeleton } from '@sales-portal/components/search-skeleton';
import { StackNavigationProp, StackScreenProps } from '@react-navigation/stack';
import { useChatSalesPortalDynamicConfigValue } from '@/experiences/sales-portal/blocs/hooks/use-chat-dynamic-config';
import { useDebouncedCallback } from 'use-debounce';
import { useFulfillmentSelection } from '@/experiences/sales-portal/domain/ordering/blocs/hooks/use-fulfillment-selection';
import { useKeyboardHotkeysContext } from '@/contexts/keyboard-shortcuts/keyboard-shortcuts-context';
import { useNavigation } from '@react-navigation/native';
import { useOrderEntryItemDetailsNavigation } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/hooks/use-order-entry-item-details-navigation';
import { useSalesPortalEmployeeRole } from '@/experiences/sales-portal/blocs/hooks/use-sales-portal-employee-role';
import { useSalesPortalExperiment } from '@/experiences/sales-portal/blocs/hooks/use-sales-portal-experiment';
import { useSalesPortalFulfillments } from '@/experiences/sales-portal/domain/ordering/blocs/hooks/use-sales-portal-fulfillments';
import { useSalesPortalRestaurantListLoader } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/loaders/use-sales-portal-restaurant-list-loader';
import { useSalesPortalSelectedRestaurantLoader } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/loaders/use-sales-portal-selected-restaurant-loader';
import { useSaveOrderFulfillmentSettings } from '@/experiences/sales-portal/domain/ordering/blocs/hooks/use-save-order-fulfillment-settings';
import { useScreenScopedKeyboardShortcuts } from '@experiences/platform/shortcuts/use-screen-scoped-keyboard-shortcuts';

type Props = StackScreenProps<
  SalesPortalStackParams,
  typeof SALES_PORTAL_ROUTES.OrderEntry
>;

const EMPTY_ORDER_GUIDE_LABELS: { level: string; orderGuideName: string }[] =
  [];
const EMPTY_PRICE_EDIT_DISPLAY_HEADERS: OrderEntryPriceEditDisplayHeader[] = [];
const ITEM_DETAIL_FOCUS_DEBOUNCE_MS = 300;

export const OrderEntryScreen: React.FC<Props> = ({ route }) => {
  const { restaurantUUID, supplierUUID, action } = route.params;
  const [styles] = useStyles();
  const { switchToExperience } = useExperienceStore();

  // Look up by supplier only
  const { enabled: isAccountsEnabled, loading: isExperimentLoading } =
    useSalesPortalExperiment({
      experimentKey: Experiments.SALES_PORTAL_ACCOUNTS_V1,
      supplierUUID,
    });

  const {
    enabled: isPriceEditingExperimentEnabled,
    loading: isPriceEditingExperimentLoading,
  } = useSalesPortalExperiment({
    experimentKey: Experiments.ORDER_ENTRY_PRICE_EDITING,
    supplierUUID,
  });

  const navigation =
    useNavigation<StackNavigationProp<SalesPortalStackParams>>();
  const { disableScope, enableScope } = useKeyboardHotkeysContext();

  const {
    activeTab,
    closePanel,
    navigationStack,
    openPanel,
    popVariantPack,
    pushVariantPack,
    selectedVariantPackUUID,
    setActiveTab,
  } = useOrderEntryItemDetailsNavigation();

  const selectedRowData = useMemo(
    () =>
      selectedVariantPackUUID
        ? (orderEntryCollection.get(selectedVariantPackUUID) ?? null)
        : null,
    [selectedVariantPackUUID],
  );

  const handleSelectTab = useCallback(
    (tab: ItemDetailTabKey) => () => setActiveTab(tab),
    [setActiveTab],
  );

  const debouncedOpenPanel = useDebouncedCallback(
    openPanel,
    ITEM_DETAIL_FOCUS_DEBOUNCE_MS,
  );

  const handleRowPress = useCallback(
    (rowData: OrderEntryTableRowData) => {
      debouncedOpenPanel.cancel();
      openPanel(rowData.variantPackUUID);
    },
    [debouncedOpenPanel, openPanel],
  );

  const handleClosePanel = useCallback(() => {
    debouncedOpenPanel.cancel();
    closePanel();
  }, [closePanel, debouncedOpenPanel]);

  const handleFocusedRowChange = useCallback(
    (rowData: OrderEntryTableRowData | null) => {
      if (
        rowData &&
        selectedVariantPackUUID &&
        rowData.variantPackUUID !== selectedVariantPackUUID
      ) {
        debouncedOpenPanel(rowData.variantPackUUID);
      }
    },
    [debouncedOpenPanel, selectedVariantPackUUID],
  );
  const [isRestaurantSelectorOpen, setIsRestaurantSelectorOpen] =
    useState(false);

  // Use useReducer to trigger re-render when error boundary retry is pressed
  const forceRerender = useReducer(() => ({}), {})[1];

  const {
    selectedRestaurant,
    loading: selectedRestaurantLoading,
    error: selectedRestaurantError,
    refetch: refetchSelectedRestaurant,
  } = useSalesPortalSelectedRestaurantLoader(restaurantUUID, supplierUUID);

  const {
    restaurantListData,
    loading: restaurantListLoading,
    error: restaurantListError,
    refetch: refetchRestaurantList,
    fetchMore: fetchMoreRestaurants,
    fetchingMore: fetchingMoreRestaurants,
    onSearch: onSearchRestaurants,
    onClear: onClearRestaurants,
  } = useSalesPortalRestaurantListLoader(
    partnerSupplier.businessOrganizationUUID,
  );

  const selectedRestaurantMissing =
    !!selectedRestaurantError ||
    (!selectedRestaurantLoading && !selectedRestaurant);

  const handleRefetchSelectedRestaurant = useCallback(() => {
    void refetchSelectedRestaurant();
    forceRerender();
  }, [refetchSelectedRestaurant, forceRerender]);

  // Set navigation title
  const selectedRestaurantName = selectedRestaurant?.restaurantName;
  useEffect(() => {
    if (selectedRestaurantName) {
      navigation.setOptions({
        title: i18n.t('sales_portal.order_entry.page_title', {
          restaurantName: selectedRestaurantName,
        }),
      });
    }
  }, [navigation, selectedRestaurantName]);

  // Derive timezones with fallback to local timezone
  const restaurantTimezone =
    selectedRestaurant?.restaurantTimezone ||
    Intl.DateTimeFormat().resolvedOptions().timeZone;
  const supplierTimezone =
    selectedRestaurant?.supplierTimezone ||
    Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Fetch employee role for current user + supplier
  const { employeeRole, loading: roleLoading } =
    useSalesPortalEmployeeRole(supplierUUID);

  // Get additional order validation roles from dynamic config (already fetched by useSalesPortalExperiment)
  const {
    value: additionalOrderValidationRolesConfig,
    loading: additionalOrderValidationRolesLoading,
  } = useChatSalesPortalDynamicConfigValue(
    supplierUUID,
    'additional_order_validation_supplier_roles',
    null,
  );

  const { value: areSubstitutionsEnabled, loading: areSubstitutionsLoading } =
    useChatSalesPortalDynamicConfigValue(
      supplierUUID,
      'are_substitutions_enabled',
      selectedRestaurant?.chatUUID ?? null,
    );
  const additionalOrderValidationRoles = useMemo(
    () => additionalOrderValidationRolesConfig ?? [],
    [additionalOrderValidationRolesConfig],
  );

  const { value: orderQuantityConfig } = useChatSalesPortalDynamicConfigValue(
    supplierUUID,
    'order_quantity_config',
    selectedRestaurant?.chatUUID ?? null,
  );
  const minMaxQuantityEnabled = isMinMaxQuantityEnabled({
    employeeRole,
    orderQuantityConfig,
  });

  const dynamicConfigLoading =
    isPriceEditingExperimentLoading ||
    additionalOrderValidationRolesLoading ||
    areSubstitutionsLoading;

  // Fulfillment settings with timezone-aware validation
  const {
    availableFulfillments,
    fulfillmentsLoading,
    getAvailabilityConstraints,
    getTimeslotOptionsForDate,
    refetch: refetchFulfillments,
    savedSelectedFulfillment,
    selectableFulfillmentOptions,
    saveSelectedFulfillment,
    timezones,
  } = useSalesPortalFulfillments(
    supplierUUID,
    restaurantUUID,
    restaurantTimezone,
    supplierTimezone,
    employeeRole,
    additionalOrderValidationRoles,
  );

  const deliveryDate = savedSelectedFulfillment?.date || null;

  const { saveFulfillment } = useSaveOrderFulfillmentSettings({
    saveSelectedFulfillment,
  });

  const fulfillmentSelection = useFulfillmentSelection({
    additionalOrderValidationRoles,
    availableFulfillments,
    employeeRole,
    savedSelectedFulfillment,
    timezones,
  });

  const handleBackToOperatorPortal = useCallback(() => {
    switchToExperience(ExperienceMode.OPERATOR_PORTAL);
  }, [switchToExperience]);

  // Keyboard shortcut: Alt+E to switch back to Operator Portal
  useScreenScopedKeyboardShortcuts(
    SalesPortalShortcuts.SwitchExperience,
    handleBackToOperatorPortal,
    [handleBackToOperatorPortal],
    {},
    true,
    SalesPortalKeyboardScope.GLOBAL,
  );

  const handleSelectRestaurant = useCallback(
    (item: RestaurantListData) => {
      const params = {
        restaurantUUID: item.key,
        supplierUUID: item.supplierUUID,
      };

      if (Platform.OS === 'web') {
        navigation.push(SALES_PORTAL_ROUTES.OrderEntry, params);
      } else {
        navigation.replace(SALES_PORTAL_ROUTES.OrderEntry, params);
      }
    },
    [navigation],
  );

  // Determine if we have all required data to show content
  // Note: When there are no fulfillments, we use a fallback (Delivery + tomorrow)
  // just like operator portal does, so we don't treat it as an error state
  const showContent =
    deliveryDate &&
    selectedRestaurant &&
    savedSelectedFulfillment &&
    !dynamicConfigLoading;

  // Stable reference - trigger derives date/type from savedSelectedFulfillment prop
  const FulfillmentTrigger = useMemo(
    () => (props: FulfillmentDropdownTriggerProps) => (
      <FulfillmentTypeAndDateTrigger {...props} />
    ),
    [],
  );

  const availabilityConstraints = useMemo(() => {
    if (!showContent) {
      return null;
    }

    return getAvailabilityConstraints(
      savedSelectedFulfillment.type,
      deliveryDate,
      selectedRestaurant.restaurantTimezone,
      selectedRestaurant.supplierTimezone,
    );
  }, [
    showContent,
    getAvailabilityConstraints,
    savedSelectedFulfillment?.type,
    deliveryDate,
    selectedRestaurant?.restaurantTimezone,
    selectedRestaurant?.supplierTimezone,
  ]);

  const handleDisableScopes = useCallback(() => {
    disableScope(SalesPortalKeyboardScope.ORDER_ENTRY_TABLE);
    disableScope(SalesPortalKeyboardScope.CHECKOUT_PANEL);
    enableScope(SalesPortalKeyboardScope.LOCATION_PICKER);
  }, [disableScope, enableScope]);

  const handleEnableScopes = useCallback(() => {
    disableScope(SalesPortalKeyboardScope.LOCATION_PICKER);
    enableScope(SalesPortalKeyboardScope.ORDER_ENTRY_TABLE);
    enableScope(SalesPortalKeyboardScope.CHECKOUT_PANEL);
  }, [disableScope, enableScope]);

  const handleNavigateToAccount = useCallback(
    (item: RestaurantListData) => {
      setIsRestaurantSelectorOpen(false);
      // Cross-tab target: AccountDetail lives in the Accounts stack, not the
      // OrderEntry stack we're currently in. push() is a stack-local action
      // and can't reach across tabs anyway; navigate via the parent tab.
      navigateToSalesPortalRoute(
        navigation,
        SALES_PORTAL_ROUTES.AccountDetail,
        {
          restaurantUUID: item.key,
          supplierUUID: item.supplierUUID,
        },
      );
    },
    [navigation],
  );

  // Keyboard shortcut: L to open restaurant selector
  const handleOpenRestaurantSelector = useCallback(() => {
    setIsRestaurantSelectorOpen(true);
    handleDisableScopes();
  }, [handleDisableScopes]);

  // Keyboard shortcut: L to open restaurant selector
  useScreenScopedKeyboardShortcuts(
    SalesPortalShortcuts.OpenRestaurantSelector,
    handleOpenRestaurantSelector,
    [handleOpenRestaurantSelector],
    {},
    true,
    SalesPortalKeyboardScope.ORDER_ENTRY_TABLE,
  );

  const shouldFetchMoreRestaurants =
    !restaurantListLoading &&
    restaurantListData.length > 0 &&
    !restaurantListError &&
    !fetchingMoreRestaurants;
  const handleFetchMoreRestaurants = useCallback(() => {
    if (shouldFetchMoreRestaurants) {
      fetchMoreRestaurants();
    }
  }, [fetchMoreRestaurants, shouldFetchMoreRestaurants]);

  const TopBarLeftActions = useMemo(() => {
    const isLoading =
      fulfillmentsLoading ||
      roleLoading ||
      !savedSelectedFulfillment ||
      !selectedRestaurant ||
      !deliveryDate ||
      !availabilityConstraints;

    if (isLoading) {
      return (
        <View style={styles.topBarLeftContainer}>
          <RestaurantSelectorSkeleton />
          <FulfillmentDropdownSkeleton />
        </View>
      );
    }

    return (
      <View style={styles.topBarLeftContainer}>
        <RestaurantSelector
          hasError={!!restaurantListError}
          isFetchingMore={fetchingMoreRestaurants}
          isLoading={restaurantListLoading}
          isOpen={isRestaurantSelectorOpen}
          listData={restaurantListData}
          onClear={onClearRestaurants}
          onClose={() => {
            setIsRestaurantSelectorOpen(false);
            handleEnableScopes();
          }}
          onEndReached={handleFetchMoreRestaurants}
          onOpen={() => {
            setIsRestaurantSelectorOpen(true);
            handleDisableScopes();
          }}
          onRetry={refetchRestaurantList}
          onSearch={onSearchRestaurants}
          onSelectItem={handleSelectRestaurant}
          renderItemRightComponent={
            isAccountsEnabled && !isExperimentLoading
              ? (item, isHoveredOrFocused) => (
                  <RestaurantNavigateButton
                    isRowHovered={isHoveredOrFocused}
                    onPress={() => handleNavigateToAccount(item)}
                  />
                )
              : undefined
          }
          selectedItem={selectedRestaurant}
        />
        <FulfillmentDropdown
          availableFulfillments={availableFulfillments}
          createIsDateDisabled={fulfillmentSelection.createIsDateDisabled}
          disabled={selectedRestaurant.onHold}
          getTimeslotCategories={fulfillmentSelection.getTimeslotCategories}
          onClose={handleEnableScopes}
          onOpen={handleDisableScopes}
          onRefetch={refetchFulfillments}
          onSave={saveFulfillment}
          restaurantUUID={restaurantUUID}
          savedSelectedFulfillment={savedSelectedFulfillment}
          selectableFulfillmentOptions={selectableFulfillmentOptions}
          timezones={timezones}
          trigger={FulfillmentTrigger}
          validateFulfillmentDate={fulfillmentSelection.validateFulfillmentDate}
          validateTimeslot={fulfillmentSelection.validateTimeslot}
        />
        <OrderEntryFulfillmentAvailability
          cutoffTimeString={availabilityConstraints.cutoffTimeString}
          orderMinimumDisplayString={
            availabilityConstraints.orderMinimumDisplayString
          }
        />
      </View>
    );
  }, [
    restaurantListData,
    restaurantListLoading,
    restaurantListError,
    refetchRestaurantList,
    fetchingMoreRestaurants,
    onSearchRestaurants,
    onClearRestaurants,
    fulfillmentsLoading,
    roleLoading,
    savedSelectedFulfillment,
    selectedRestaurant,
    deliveryDate,
    availabilityConstraints,
    styles.topBarLeftContainer,
    isRestaurantSelectorOpen,
    handleSelectRestaurant,
    handleNavigateToAccount,
    isAccountsEnabled,
    isExperimentLoading,
    availableFulfillments,
    selectableFulfillmentOptions,
    fulfillmentSelection.createIsDateDisabled,
    fulfillmentSelection.getTimeslotCategories,
    fulfillmentSelection.validateFulfillmentDate,
    fulfillmentSelection.validateTimeslot,
    handleEnableScopes,
    handleDisableScopes,
    handleFetchMoreRestaurants,
    refetchFulfillments,
    saveFulfillment,
    restaurantUUID,
    timezones,
    FulfillmentTrigger,
  ]);

  const LoadingSkeleton = () => (
    <View style={styles.loadingContent}>
      <SearchSkeleton />
      <OrderEntryTableSkeleton />
    </View>
  );

  const ErrorComponent = () => (
    <ErrorState
      ctaProps={{ disabled: selectedRestaurantLoading }}
      description={i18n.t('sales_portal.order_entry.error.table.description')}
      onPress={handleRefetchSelectedRestaurant}
      title={i18n.t('sales_portal.order_entry.error.table.title')}
    />
  );

  const hasVariantPacksInNavigationStack = navigationStack.length > 0;

  return (
    <SalesHubShellTemplate>
      <ContentWithPanelTemplate
        onClose={handleClosePanel}
        panelContent={
          selectedVariantPackUUID && selectedRestaurant && deliveryDate ? (
            <ItemDetailContent
              activeTab={activeTab}
              chatUUID={selectedRestaurant.chatUUID ?? null}
              deliveryDate={deliveryDate}
              FooterComponent={
                hasVariantPacksInNavigationStack
                  ? ItemDetailPanelFooter
                  : undefined
              }
              isQuantityDisabled={selectedRestaurant.onHold}
              key={selectedVariantPackUUID}
              labels={selectedRowData?.labels}
              onBack={
                hasVariantPacksInNavigationStack ? popVariantPack : undefined
              }
              onClose={handleClosePanel}
              onSelectTab={handleSelectTab}
              onSelectVariantPack={pushVariantPack}
              orderGuideLabels={
                selectedRowData?.orderGuideLabels ?? EMPTY_ORDER_GUIDE_LABELS
              }
              priceEditDisplayHeaders={
                selectedRowData?.priceEditDisplayHeaders ??
                EMPTY_PRICE_EDIT_DISPLAY_HEADERS
              }
              restaurantId={restaurantUUID}
              restaurantTimezone={restaurantTimezone}
              supplierId={supplierUUID}
              variantPackId={selectedVariantPackUUID}
            />
          ) : undefined
        }
      >
        {selectedRestaurant?.onHold ? (
          <SalesPortalBanner
            message={i18n.t('sales_portal.order_entry.account_on_hold')}
          />
        ) : null}
        <AppPageLayout
          actions={<ConfidentialFieldToggle />}
          leadingActions={TopBarLeftActions}
          scroll={false}
        >
          <View
            // Dismiss keyboard on tap outside text input (native/tablet only).
            // Returns false so child touches are not blocked.
            onStartShouldSetResponder={() => {
              if (Platform.OS !== 'web') {
                Keyboard.dismiss();
              }
              return false;
            }}
            style={[
              styles.content,
              selectedRestaurantMissing ? styles.emptyContent : undefined,
            ]}
          >
            {!showContent ? (
              selectedRestaurantMissing ? (
                <ErrorComponent />
              ) : (
                <LoadingSkeleton />
              )
            ) : (
              <OrderEntryErrorBoundary
                fallback={<ErrorComponent />}
                key={`${restaurantUUID}-${supplierUUID}`}
              >
                <Suspense fallback={<LoadingSkeleton />}>
                  <OrderEntryScreenContent
                    action={action}
                    additionalOrderValidationRoles={
                      additionalOrderValidationRoles
                    }
                    areSubstitutionsEnabled={!!areSubstitutionsEnabled}
                    availabilityConstraints={availabilityConstraints}
                    availableFulfillments={availableFulfillments}
                    employeeRole={employeeRole}
                    getTimeslotOptionsForDate={getTimeslotOptionsForDate}
                    handleSelectedFulfillment={saveFulfillment}
                    isPriceEditingExperimentEnabled={
                      isPriceEditingExperimentEnabled
                    }
                    minMaxQuantityEnabled={minMaxQuantityEnabled}
                    onFocusedRowChange={handleFocusedRowChange}
                    onOpenRestaurantSelector={handleOpenRestaurantSelector}
                    onRowPress={handleRowPress}
                    refetchFulfillments={refetchFulfillments}
                    restaurantUUID={restaurantUUID}
                    savedSelectedFulfillment={savedSelectedFulfillment}
                    selectableFulfillmentOptions={selectableFulfillmentOptions}
                    selectedRestaurant={selectedRestaurant}
                    supplierUUID={supplierUUID}
                    timezones={timezones}
                  />
                </Suspense>
              </OrderEntryErrorBoundary>
            )}
          </View>
        </AppPageLayout>
      </ContentWithPanelTemplate>
    </SalesHubShellTemplate>
  );
};

const useStyles = createStyles((theme) => ({
  content: {
    flex: 1,
    gap: theme.sk.spacing.spaceSMedium,
    // prevent bottom bar transition from displaying scrollbar
    overflowY: 'hidden',
    paddingTop: theme.sk.spacing.spaceMedium,
  },
  emptyContent: {
    alignItems: 'center',
    gap: 0,
    justifyContent: 'center',
  },
  loadingContent: {
    flex: 1,
    gap: 42,
  },
  topBarLeftContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSmall,
  },
}));
